'use client';

import React from 'react';
import { ArrowLeft, Clock, ChevronRight } from 'lucide-react';
import { Category, Service } from './types';

interface CategoryServicesViewProps {
  category: Category;
  services: Service[];
  onBack: () => void;
  onSelectService: (service: Service) => void;
}

export const CategoryServicesView: React.FC<CategoryServicesViewProps> = ({
  category,
  services,
  onBack,
  onSelectService,
}) => {
  const categoryServices = services.filter(
    (s) => s.category === category.name || s.categoryId === category.id
  );

  return (
    <div className="w-full max-w-xl mx-auto px-4 pb-20">
      {/* Barra de cabecera con botón volver */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          aria-label="Volver a categorías"
          className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 transition shadow-sm cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-[#0f172a] uppercase tracking-wide">
            {category.name}
          </h2>
          <p className="text-xs text-slate-500">
            {categoryServices.length} {categoryServices.length === 1 ? 'servicio disponible' : 'servicios disponibles'}
          </p>
        </div>
      </div>

      {/* Lista de tarjetas de servicio */}
      <div className="flex flex-col gap-3.5">
        {categoryServices.map((service) => {
          const defaultImg =
            service.imageUrl ||
            'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=500&h=400&fit=crop';

          return (
            <div
              key={service.id}
              onClick={() => onSelectService(service)}
              className="group bg-white rounded-2xl p-4 shadow-sm border border-slate-100/90 hover:shadow-md hover:border-slate-300 transition duration-200 cursor-pointer flex gap-4 items-center"
            >
              {/* Miniatura cuadrada */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden shrink-0 bg-slate-100">
                <img
                  src={defaultImg}
                  alt={service.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                />
              </div>

              {/* Información del servicio */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-bold text-[#0f172a] group-hover:text-emerald-700 transition line-clamp-1">
                  {service.name}
                </h3>
                {service.description && (
                  <p className="text-xs text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">
                    {service.description}
                  </p>
                )}

                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="inline-flex items-center gap-1 font-medium text-slate-500">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    {service.duration} min
                  </span>

                  <span className="font-extrabold text-[#0f172a] text-sm sm:text-base">
                    ${' '}
                    {Number(service.cashPrice || 0).toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </div>
              </div>

              {/* Flecha selectora */}
              <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-[#0f172a] group-hover:text-white flex items-center justify-center text-slate-500 transition shrink-0">
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
